<!-- Шаг грузов в мастере создания заказа: создает Cargo и позиции Cargo::Item по order_id. -->
<template>
  <div class="order-create-container">
    <base-loader v-if="dataIsLoading" />

    <div
      v-else
      class="card order-cargo-step"
    >
      <div class="order-block">
        <BaseFormSection title="Грузы" icon="cargo" columns="1">
          <OrderCargoSummary
            class="order-cargo-step__summary"
            :items="cargoItems"
          />

          <div class="order-cargo-step__list">
            <template
              v-for="(item, index) in cargoItems"
              :key="item.key"
            >
              <OrderCargoItemCard
                :errors="errors"
                :index="index"
                :item="item"
                :registry-items="registryItems"
                :removable="cargoItems.length > 1"
                @create-registry-item="openCreateRegistryItem"
                @edit-registry-item="openEditRegistryItem"
                @remove="removeCargoItem(index)"
                @update-field="updateCargoItem"
              />
            </template>
          </div>

          <div class="order-cargo-step__add-row">
            <base-primary-button
              type="button"
              class="order-cargo-step__add-button"
              @click="addCargoItem"
            >
              <AppIcon name="addSquare" :size="16" />
              Добавить груз
            </base-primary-button>
          </div>
        </BaseFormSection>
      </div>

      <BaseFormFooter
        :actions="footerActions"
        :missing-field-groups="missingFieldGroups"
        :is-missing-fields-expanded="isMissingFieldsExpanded"
        @action="handleFooterAction"
        @select-missing-field="handleMissingFieldSelect"
      >
        <template #info>
          <BaseMissingFieldsPanel
            :count="missingFieldsRawCount"
            :is-expanded="isMissingFieldsExpanded"
            @toggle="isMissingFieldsExpanded = !isMissingFieldsExpanded"
          />
        </template>
      </BaseFormFooter>
    </div>

    <CargoItemRegistryModal
      :visible="registryEditorVisible"
      :title="registryEditorTitle"
      :initial-data="registryEditor.initialData"
      :is-loading="isRegistrySaving"
      :submit-label="registryEditorSubmitLabel"
      @save="saveRegistryItem"
      @close="closeRegistryEditor"
    />
  </div>
</template>

<script>
import { mapState } from "pinia";

import { getCompanyId } from "@/utils/userUtils";
import { currentDateOnly } from "@/utils/dateUtils";
import { useCargoItemRegistryStore } from "@/stores/cargoItemRegistryStore";
import { useNotificationStore } from "@/stores/notificationStore";
import {
  cargoNumber,
  cargoModelDimensionM,
  grossWeightKgFromCargoItem,
} from "@/utils/cargoCalculatorUtils";

import cargoItemsApi from "@/services/api/cargoItemsApi";
import tripCargosApi from "@/services/api/tripCargosApi";

import AppIcon from "@/components/Icon/AppIcon.vue";
import CargoItemRegistryModal from "@/components/Cargo/CargoItemRegistryModal.vue";
import OrderCargoItemCard from "@/components/Order/OrderCargoItemCard.vue";
import OrderCargoSummary from "@/components/Order/OrderCargoSummary.vue";

export default {
  name: "Step2Cargo",

  components: {
    AppIcon,
    CargoItemRegistryModal,
    OrderCargoItemCard,
    OrderCargoSummary,
  },

  props: {
    orderId: {
      type: [String, Number],
      required: true,
    },
    showBackAction: {
      type: Boolean,
      default: true,
    },
    submitLabel: {
      type: String,
      default: "Далее",
    },
    proceedOnSave: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      companyId: getCompanyId(),
      dataIsLoading: false,
      isSubmitting: false,
      isRegistrySaving: false,
      isMissingFieldsExpanded: false,
      cargos: [],
      cargoItems: [this.emptyCargoItem()],
      removedCargoItemIds: [],
      errors: {},
      registryEditor: {
        mode: null,
        itemIndex: null,
        itemId: null,
        initialData: null,
      },
    };
  },

  computed: {
    ...mapState(useCargoItemRegistryStore, {
      registryItems: "items",
    }),

    notificationStore() {
      return useNotificationStore();
    },

    missingFieldGroups() {
      const items = [];

      this.cargoItems.forEach((item, index) => {
        const prefix = `Груз ${index + 1}`;
        if (!String(item.title || "").trim()) {
          items.push(`${prefix}: наименование`);
        }
        if (!cargoNumber(item.quantity)) {
          items.push(`${prefix}: количество мест`);
        }
        if (!cargoNumber(item.gross_weight_kg)) {
          items.push(`${prefix}: масса брутто`);
        }
        if (!cargoNumber(item.height_m)) {
          items.push(`${prefix}: высота`);
        }
        if (!cargoNumber(item.width_m)) {
          items.push(`${prefix}: ширина`);
        }
        if (!cargoNumber(item.length_m)) {
          items.push(`${prefix}: длина`);
        }
        if (!cargoNumber(item.volume_m3)) {
          items.push(`${prefix}: объём места`);
        }
      });

      if (!items.length) return [];

      return [
        {
          key: "cargo",
          title: "Грузы",
          sectionRef: "cargo",
          items,
        },
      ];
    },

    missingFieldsRawCount() {
      return this.missingFieldGroups.reduce(
        (sum, group) => sum + group.items.length,
        0
      );
    },

    footerActions() {
      const actions = [];

      if (this.showBackAction) {
        actions.push({
          key: "back",
          label: "Назад",
          disabled: this.isSubmitting,
        });
      }

      actions.push({
        key: "save",
        label: this.isSubmitting ? "Сохранение..." : this.submitLabel,
        variant: "primary",
        disabled: this.isSubmitting,
      });

      return actions;
    },

    registryEditorTitle() {
      return this.registryEditor.mode === "edit"
        ? "Редактирование позиции реестра"
        : "Новая позиция реестра";
    },

    registryEditorVisible() {
      return Boolean(this.registryEditor.mode);
    },

    registryEditorSubmitLabel() {
      return this.registryEditor.mode === "edit"
        ? "Сохранить и выбрать"
        : "Создать и выбрать";
    },
  },

  methods: {
    async loadData() {
      this.dataIsLoading = true;

      try {
        await Promise.all([
          this.loadRegistryItems(),
          this.loadOrderCargos(),
        ]);
      } finally {
        this.dataIsLoading = false;
      }
    },

    async loadRegistryItems() {
      const store = useCargoItemRegistryStore();
      await store.getItemsRequest();
    },

    async loadOrderCargos() {
      const response = await tripCargosApi.index(this.companyId, {
        "q[order_id]": this.orderId,
      });
      this.cargos = response.data.result || response.data || [];

      const cargoItems = await this.loadCargoItems(this.cargos);
      this.cargoItems = cargoItems.length
        ? cargoItems
        : [this.emptyCargoItem()];
    },

    async loadCargoItems(cargos) {
      const itemGroups = await Promise.all(
        cargos.map(async (cargo) => {
          const response = await cargoItemsApi.index(this.companyId, {
            "q[cargo_id]": cargo.id,
          });
          const items = response.data.result || response.data || [];

          return items.map((item) => this.cargoItemFromApi(item, cargo));
        })
      );

      return itemGroups.flat();
    },

    addCargoItem() {
      this.cargoItems = [...this.cargoItems, this.emptyCargoItem()];
    },

    removeCargoItem(index) {
      const item = this.cargoItems[index];
      if (!item) return;

      if (item.id) this.removedCargoItemIds.push(item.id);

      this.cargoItems = this.cargoItems.filter((_, i) => i !== index);
      if (!this.cargoItems.length) {
        this.cargoItems = [this.emptyCargoItem()];
      }
      this.rebuildErrorsAfterRemove();
    },

    updateCargoItem({ index, field, value, values }) {
      const item = this.cargoItems[index];
      if (!item) return;

      const nextItem = {
        ...item,
        ...(values || { [field]: value }),
      };
      const initialGrossWeight = nextItem.gross_weight_kg;
      this.syncGrossWeight(nextItem, field, values);

      this.cargoItems.splice(index, 1, nextItem);
      const changedFields = Object.keys(values || { [field]: value });
      if (nextItem.gross_weight_kg !== initialGrossWeight) {
        changedFields.push("gross_weight_kg");
      }

      changedFields.forEach((key) => {
        this.clearError(this.errorKey(index, key));
      });
    },

    openCreateRegistryItem(index) {
      this.registryEditor = {
        mode: "create",
        itemIndex: index,
        itemId: null,
        initialData: null,
      };
    },

    async openEditRegistryItem({ index, id }) {
      const store = useCargoItemRegistryStore();
      const initialData =
        this.registryItems.find((item) => String(item.id) === String(id)) ||
        (await store.showItem(id));

      if (!initialData) return;

      this.registryEditor = {
        mode: "edit",
        itemIndex: index,
        itemId: id,
        initialData,
      };
    },

    closeRegistryEditor() {
      this.registryEditor = {
        mode: null,
        itemIndex: null,
        itemId: null,
        initialData: null,
      };
    },

    async saveRegistryItem(payload) {
      const store = useCargoItemRegistryStore();
      const { mode, itemIndex, itemId } = this.registryEditor;

      if (itemIndex === null || itemIndex === undefined) return;

      this.isRegistrySaving = true;
      try {
        let registryItem;

        if (mode === "edit") {
          await store.updateItem(itemId, payload);
          registryItem = await store.showItem(itemId);
        } else {
          registryItem = await store.createItem(payload);
        }

        this.applyRegistryItemToCargo(itemIndex, registryItem);
        this.closeRegistryEditor();
        this.notificationStore.showSuccess(
          mode === "edit"
            ? "Позиция реестра обновлена и выбрана"
            : "Позиция реестра создана и выбрана"
        );
      } finally {
        this.isRegistrySaving = false;
      }
    },

    applyRegistryItemToCargo(index, registryItem) {
      if (!registryItem) return;

      this.updateCargoItem({
        index,
        values: {
          cargo_item_registry_id: registryItem.id,
          cargo_unit_id: registryItem.cargo_unit_id || null,
          title: registryItem.title || "",
          description: registryItem.description || "",
          quantity: registryItem.quantity ?? "",
          weight_kg: registryItem.weight_kg ?? "",
          gross_weight_kg:
            grossWeightKgFromCargoItem(registryItem) ?? "",
          height_m: cargoModelDimensionM(registryItem, "height"),
          width_m: cargoModelDimensionM(registryItem, "width"),
          length_m: cargoModelDimensionM(registryItem, "length"),
          volume_m3: registryItem.volume_m3 ?? "",
          auto_calculated_volume_m3: registryItem.volume_m3 ?? "",
          auto_calculated_gross_weight_kg:
            grossWeightKgFromCargoItem(registryItem) ?? "",
        },
      });
    },

    async saveCargoAndProceed() {
      if (!this.validate()) {
        this.isMissingFieldsExpanded = true;
        this.$nextTick(() => this.handleMissingFieldSelect());
        return;
      }

      this.isSubmitting = true;

      try {
        await this.deleteRemovedCargoItems();
        await this.saveCargoItems();

        this.notificationStore.showSuccess("Грузы заказа сохранены");
        this.$emit("saved");
        if (this.proceedOnSave) this.goToNextStep();
      } catch (error) {
        console.error("Ошибка при сохранении грузов заказа", error);
      } finally {
        this.isSubmitting = false;
      }
    },

    async deleteRemovedCargoItems() {
      await Promise.all(
        this.removedCargoItemIds.map((id) =>
          cargoItemsApi.delete(this.companyId, id)
        )
      );

      this.removedCargoItemIds = [];
    },

    async saveCargoItems() {
      for (const item of this.cargoItems) {
        const cargoId = item.cargo_id || (await this.createCargo()).id;
        const payload = this.cargoItemPayload(item, cargoId);

        if (item.id) {
          await cargoItemsApi.update(this.companyId, item.id, payload);
        } else {
          await cargoItemsApi.create(this.companyId, payload);
        }
      }
    },

    async createCargo() {
      const response = await tripCargosApi.create(this.companyId, {
        order_id: this.orderId,
        cargo_date: currentDateOnly(),
      });

      return response.data.result || response.data || {};
    },

    validate() {
      this.errors = {};

      this.cargoItems.forEach((item, index) => {
        if (!String(item.title || "").trim()) {
          this.errors[this.errorKey(index, "title")] = true;
        }
        if (!cargoNumber(item.quantity)) {
          this.errors[this.errorKey(index, "quantity")] = true;
        }
        if (!cargoNumber(item.gross_weight_kg)) {
          this.errors[this.errorKey(index, "gross_weight_kg")] = true;
        }
        if (!cargoNumber(item.height_m)) {
          this.errors[this.errorKey(index, "height_m")] = true;
        }
        if (!cargoNumber(item.width_m)) {
          this.errors[this.errorKey(index, "width_m")] = true;
        }
        if (!cargoNumber(item.length_m)) {
          this.errors[this.errorKey(index, "length_m")] = true;
        }
        if (!cargoNumber(item.volume_m3)) {
          this.errors[this.errorKey(index, "volume_m3")] = true;
        }
      });

      return Object.keys(this.errors).length === 0;
    },

    handleFooterAction(action) {
      if (action === "back") {
        this.goToPreviousStep();
      }

      if (action === "save") {
        this.saveCargoAndProceed();
      }
    },

    handleMissingFieldSelect() {
      const element =
        this.$el.querySelector(".invalid, .error-message") ||
        this.$el.querySelector(".order-cargo-step__list") ||
        this.$el;

      const formGroup = element.closest?.(".form-group");
      (formGroup || element).scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      this.isMissingFieldsExpanded = false;
    },

    clearError(key) {
      if (this.errors[key]) {
        const nextErrors = { ...this.errors };
        delete nextErrors[key];
        this.errors = nextErrors;
      }
    },

    rebuildErrorsAfterRemove() {
      if (!Object.keys(this.errors).length) return;
      this.validate();
    },

    cargoItemPayload(item, cargoId) {
      return {
        cargo_id: cargoId,
        cargo_item_registry_id: item.cargo_item_registry_id,
        cargo_unit_id: item.cargo_unit_id || null,
        title: item.title,
        description: item.description || null,
        quantity: Number(cargoNumber(item.quantity)),
        weight_kg: cargoNumber(item.weight_kg),
        gross_weight_kg: cargoNumber(item.gross_weight_kg),
        height_m: cargoNumber(item.height_m),
        width_m: cargoNumber(item.width_m),
        length_m: cargoNumber(item.length_m),
        volume_m3: cargoNumber(item.volume_m3),
      };
    },

    cargoItemFromApi(item, cargo) {
      return {
        key: `item-${item.id}`,
        id: item.id,
        cargo_id: cargo.id,
        cargo_item_registry_id: item.cargo_item_registry_id || null,
        cargo_unit_id: item.cargo_unit_id || null,
        title: item.title || "",
        description: item.description || "",
        quantity: item.quantity ?? "",
        weight_kg: item.weight_kg ?? "",
        gross_weight_kg: item.gross_weight_kg ?? "",
        height_m: cargoModelDimensionM(item, "height"),
        width_m: cargoModelDimensionM(item, "width"),
        length_m: cargoModelDimensionM(item, "length"),
        volume_m3: item.volume_m3 ?? "",
        auto_calculated_volume_m3: item.volume_m3 ?? "",
        auto_calculated_gross_weight_kg: item.gross_weight_kg ?? "",
      };
    },

    emptyCargoItem() {
      return {
        key: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        id: null,
        cargo_id: null,
        cargo_item_registry_id: null,
        cargo_unit_id: null,
        title: "",
        description: "",
        quantity: "",
        weight_kg: "",
        gross_weight_kg: "",
        height_m: "",
        width_m: "",
        length_m: "",
        volume_m3: "",
        auto_calculated_volume_m3: "",
        auto_calculated_gross_weight_kg: "",
      };
    },

    syncGrossWeight(item, field, values) {
      const changedFields = Object.keys(values || { [field]: true });
      if (!changedFields.some((key) => ["quantity", "weight_kg"].includes(key))) return;

      const calculatedGrossWeight = grossWeightKgFromCargoItem(item);
      if (!calculatedGrossWeight) return;

      const currentGrossWeight = cargoNumber(item.gross_weight_kg);
      const previousAutoGrossWeight = cargoNumber(
        item.auto_calculated_gross_weight_kg
      );
      if (
        currentGrossWeight &&
        previousAutoGrossWeight &&
        currentGrossWeight !== previousAutoGrossWeight
      ) {
        return;
      }

      item.gross_weight_kg = calculatedGrossWeight;
      item.auto_calculated_gross_weight_kg = calculatedGrossWeight;
    },

    errorKey(index, field) {
      return `cargoItems.${index}.${field}`;
    },

    goToPreviousStep() {
      this.$router.push({
        name: "OrderCreateStep1",
        query: { id: this.orderId },
      });
    },

    goToNextStep() {
      this.$router.push({
        name: "OrderCreateStep3",
        query: { id: this.orderId },
      });
    },
  },

  created() {
    this.loadData();
  },
};
</script>

<style scoped>
.order-cargo-step {
  padding: var(--base-in-indent);
}

.order-cargo-step__list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  align-items: start;
  min-width: 0;
}

.order-cargo-step__summary {
  margin-bottom: 0;
}

.order-cargo-step__add-row {
  display: flex;
  justify-content: flex-start;
  min-width: 0;
}

.order-cargo-step__add-button {
  gap: 6px;
}

.order-cargo-step__add-button :deep(svg) {
  display: block;
  flex: 0 0 auto;
}

@media (max-width: 760px) {
  .order-cargo-step__add-row {
    justify-content: stretch;
  }

  .order-cargo-step__add-button {
    width: 100%;
  }
}
</style>
